import { useEffect, useState } from 'react';
import { Home } from './pages/Home';
import { Search } from './pages/Search';
import { Company } from './pages/Company';
import { Compare } from './pages/Compare';
import { Monitoring } from './pages/Monitoring';
import { IP } from './pages/IP';
import { People } from './pages/People';
import { Pricing } from './pages/Pricing';
import { FsspCheck } from './pages/FsspCheck';

/**
 * Простой hash-based router без react-router-dom (избегаем лишней зависимости).
 *
 * Routes:
 *   #/                         → Home
 *   #/search                   → Search
 *   #/search?q=...             → Search с initialQuery
 *   #/company/{slug-or-inn}    → Company
 *   #/compare?inns=A,B,C       → Compare 2-5 компаний side-by-side
 *
 * Для production migration на BrowserRouter — после полной миграции остальных страниц.
 */
type Route =
  | { name: 'home' }
  | { name: 'search'; q: string }
  | { name: 'company'; slug: string }
  | { name: 'ip'; slug: string }
  | { name: 'person'; slug: string }
  | { name: 'compare'; inns: string[] }
  | { name: 'monitoring' }
  | { name: 'pricing' }
  | { name: 'fssp'; inn?: string }
  | { name: '404' };

function parseHash(hash: string): Route {
  const h = hash.replace(/^#/, '') || '/';
  if (h === '/' || h === '/home' || h === '') return { name: 'home' };
  const searchMatch = h.match(/^\/search(?:\?q=(.+))?$/);
  if (searchMatch) {
    return { name: 'search', q: decodeURIComponent(searchMatch[1] ?? '') };
  }
  const companyMatch = h.match(/^\/company\/([^/?]+)/);
  if (companyMatch) {
    return { name: 'company', slug: decodeURIComponent(companyMatch[1]!) };
  }
  const ipMatch = h.match(/^\/ip\/([^/?]+)/);
  if (ipMatch) {
    return { name: 'ip', slug: decodeURIComponent(ipMatch[1]!) };
  }
  const personMatch = h.match(/^\/(?:person|people)\/([^/?]+)/);
  if (personMatch) {
    return { name: 'person', slug: decodeURIComponent(personMatch[1]!) };
  }
  if (h === '/pricing') {
    return { name: 'pricing' };
  }
  const fsspMatch = h.match(/^\/fssp-check(?:\?inn=(\d+))?$/);
  if (fsspMatch) {
    return { name: 'fssp', inn: fsspMatch[1] };
  }
  const compareMatch = h.match(/^\/compare(?:\?inns=(.+))?$/);
  if (compareMatch) {
    const inns = (compareMatch[1] ?? '')
      .split(',')
      .map((s) => decodeURIComponent(s).trim())
      .filter(Boolean);
    return { name: 'compare', inns };
  }
  if (h === '/monitoring' || h.startsWith('/monitoring')) {
    return { name: 'monitoring' };
  }
  return { name: '404' };
}

/**
 * Path-router для cutover (ADR-0015): /company/:slug и др. читаются из pathname
 * (Vite-карточка на основном домене profilecompany.ru). Hash (#/...) — fallback
 * для legacy-ссылок и dev-preview.
 */
/** decodeURIComponent бросает URIError на malformed (%E0%A4%A) — не роняем роутер. */
function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

function parseRoute(): Route {
  if (typeof window !== 'undefined' && window.location.pathname) {
    const p = window.location.pathname.replace(/\/+$/, '') || '/'; // нормализуем trailing slash
    const company = p.match(/^\/company\/([^/?#]+)$/);
    if (company) return { name: 'company', slug: safeDecode(company[1]!) };
    const ip = p.match(/^\/ip\/([^/?#]+)$/);
    if (ip) return { name: 'ip', slug: safeDecode(ip[1]!) };
    const person = p.match(/^\/(?:person|people)\/([^/?#]+)$/);
    if (person) return { name: 'person', slug: safeDecode(person[1]!) };
    if (p === '/fssp-check') {
      const inn = new URLSearchParams(window.location.search).get('inn') ?? undefined;
      return { name: 'fssp', inn: inn && /^\d{10,12}$/.test(inn) ? inn : undefined };
    }
  }
  return parseHash(window.location.hash);
}

export function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute());

  useEffect(() => {
    const handler = () => setRoute(parseRoute());
    window.addEventListener('hashchange', handler);
    window.addEventListener('popstate', handler); // path-навигация (cutover)
    return () => {
      window.removeEventListener('hashchange', handler);
      window.removeEventListener('popstate', handler);
    };
  }, []);

  if (route.name === 'home') return <Home />;
  if (route.name === 'search') return <Search initialQuery={route.q} />;
  if (route.name === 'company') return <Company slug={route.slug} />;
  if (route.name === 'ip') return <IP slug={route.slug} />;
  if (route.name === 'person') return <People slug={route.slug} />;
  if (route.name === 'pricing') return <Pricing />;
  if (route.name === 'fssp') return <FsspCheck initialInn={route.inn} />;
  if (route.name === 'compare') return <Compare initialInns={route.inns} />;
  if (route.name === 'monitoring') {
    // Token из localStorage (auth flow появится в migration позже)
    const token = typeof window !== 'undefined' ? window.localStorage.getItem('pcp_access_token') : null;
    return <Monitoring token={token} />;
  }
  return (
    <main className="container" style={{ paddingTop: 64, textAlign: 'center' }}>
      <h1>404 — Страница не найдена</h1>
      <p style={{ color: 'var(--muted, #6b7287)' }}>
        <a href="#/">На главную</a>
      </p>
    </main>
  );
}
